import { lazy, Suspense } from "react";
import PageLoader from "@/components/PageLoader";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ConfirmHost } from "@/components/tracker/confirm";
import { ReviewGuardHost, CompletionHost } from "@/components/tracker/reviewGuard";
import { AuthProvider } from "@/context/AuthContext";
import LandingPage from "@/components/landing/LandingPage";
import Login from "@/pages/Login";
const Signup = lazy(() => import("@/pages/Signup"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const About = lazy(() => import("@/pages/About"));
const Privacy = lazy(() => import("@/pages/legal/Privacy"));
const Terms = lazy(() => import("@/pages/legal/Terms"));
const Changelog = lazy(() => import("@/pages/Changelog"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
import ProtectedRoute from "@/components/tracker/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import PageTracker from "@/components/PageTracker";
import PlanGate from "@/components/tracker/PlanGate";
import TrackerLayout from "@/components/tracker/TrackerLayout";
import Dashboard from "@/pages/tracker/Dashboard";
const MyWork = lazy(() => import("@/pages/tracker/MyWork"));
const ReviewQueue = lazy(() => import("@/pages/tracker/ReviewQueue"));
const Announcements = lazy(() => import("@/pages/tracker/Announcements"));
const Board = lazy(() => import("@/pages/tracker/Board"));
const Timeline = lazy(() => import("@/pages/tracker/Timeline"));
const Mindmap = lazy(() => import("@/pages/tracker/Mindmap"));
const Tasks = lazy(() => import("@/pages/tracker/Tasks"));
const Pto = lazy(() => import("@/pages/tracker/Pto"));
const Projects = lazy(() => import("@/pages/tracker/Projects"));
const Portfolio = lazy(() => import("@/pages/tracker/Portfolio"));
const Team = lazy(() => import("@/pages/tracker/Team"));
const Audit = lazy(() => import("@/pages/tracker/Audit"));
const Report = lazy(() => import("@/pages/tracker/Report"));
const Billing = lazy(() => import("@/pages/tracker/Billing"));
const OwnerConsole = lazy(() => import("@/pages/tracker/OwnerConsole"));

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <PageTracker />
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <TrackerLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="*" element={<Navigate to="/app" replace />} />
            <Route path="my-work" element={<MyWork />} />
            <Route path="review" element={<PlanGate feature="review"><ReviewQueue /></PlanGate>} />
            <Route path="announcements" element={<PlanGate feature="announcements"><Announcements /></PlanGate>} />
            <Route path="board" element={<Board />} />
            <Route
              path="timeline"
              element={
                <PlanGate feature="timeline">
                  <Timeline />
                </PlanGate>
              }
            />
            <Route
              path="mindmap"
              element={
                <PlanGate feature="mindmap">
                  <Mindmap />
                </PlanGate>
              }
            />
            <Route path="tasks" element={<Tasks />} />
            <Route
              path="pto"
              element={
                <PlanGate feature="pto">
                  <Pto />
                </PlanGate>
              }
            />
            <Route
              path="owner"
              element={
                <ProtectedRoute platformOnly>
                  <OwnerConsole />
                </ProtectedRoute>
              }
            />
            <Route
              path="billing"
              element={
                <ProtectedRoute ownerOnly>
                  <Billing />
                </ProtectedRoute>
              }
            />
            <Route
              path="projects"
              element={<Projects />}
            />
            <Route
              path="portfolio"
              element={
                <ProtectedRoute adminOnly>
                  <Portfolio />
                </ProtectedRoute>
              }
            />
            <Route
              path="team"
              element={
                <ProtectedRoute adminOnly>
                  <Team />
                </ProtectedRoute>
              }
            />
            <Route
              path="audit"
              element={
                <ProtectedRoute adminOnly>
                  <PlanGate feature="audit">
                    <Audit />
                  </PlanGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="reports"
              element={
                <ProtectedRoute adminOnly>
                  <PlanGate feature="reports">
                    <Report />
                  </PlanGate>
                </ProtectedRoute>
              }
            />
          </Route>
        </Routes>
        </Suspense>
        <Toaster position="bottom-center" theme="dark" />
        <ConfirmHost />
        <ReviewGuardHost />
        <CompletionHost />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
